import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import { API_URL, CIP_APP_URL } from '../components/config';
import HarvestingTopNav from '../components/HarvestingTopNav';
import { getViewAsMode } from '../utils/viewMode';
import './HarvestingPage.css';

const resolveCipBaseUrl = (...candidates) => {
  for (const candidate of candidates) {
    try {
      const url = new URL(String(candidate || '').trim());
      const isLocal = ['localhost', '127.0.0.1', '::1'].includes(url.hostname);
      if (process.env.REACT_APP_ENV === 'prod' && isLocal) continue;
      return url.origin;
    } catch {
      // Try the next configured value.
    }
  }
  return '';
};

const HarvestingPage = () => {
  const location = useLocation();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatesCollapsed, setUpdatesCollapsed] = useState(true);
  const [landingPage, setLandingPage] = useState(null);
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const viewAsMode = getViewAsMode(location.search);
  const previewMode = Boolean(viewAsMode);
  const previewQuery = viewAsMode ? `?viewAs=${viewAsMode}` : '';

  const stripHtml = (value) => {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    const fetchLandingPage = async () => {
      try {
        const response = previewMode
          ? await axios.get(`${API_URL}/api/harvesting-pages/landing`)
          : await api.get('/api/harvesting-pages/landing');
        setLandingPage(response.data.page || null);
      } catch (err) {
        setLandingPage(null);
      }
    };
    fetchLandingPage();
  }, [previewMode]);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        // Main landing page shows every post regardless of topic; topic pages
        // additionally show their own filtered subset via pageSlug.
        const response = previewMode
          ? await axios.get(`${API_URL}/api/harvesting-posts/public`)
          : await api.get('/api/harvesting-posts/public');
        setBlogPosts((response.data.posts || []).slice(0, 3));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching harvesting posts:', err);
        setError(err.response?.data?.message || 'Error fetching blog posts');
        setLoading(false);
      }
    };
    fetchBlogPosts();
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) {
      setConfigLoading(false);
      return;
    }

    const fetchConnectorConfig = async () => {
      setConfigLoading(true);
      try {
        const res = await api.get('/api/connector/config');
        setConnectorConfig(res.data?.config || null);
      } catch {
        setConnectorConfig(null);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConnectorConfig();
  }, [previewMode]);

  const authToken = localStorage.getItem('authToken');
  const embedCfg = connectorConfig?.embedSettings || {};
  const cipIntegrationEnabled = Boolean(embedCfg.cipIntegrationEnabled);
  const cipConfiguredBase = resolveCipBaseUrl(embedCfg.cipBaseUrl, CIP_APP_URL);
  const cipReady = !!cipConfiguredBase;
  const canBridge = cipReady && !!authToken;
  const useCipHarvesting = canBridge && (cipIntegrationEnabled || previewMode);
  const embeddedHarvestReportPath = '/harvesting/pages/harvest-report?cipView=map';
  const showCipEmbed = cipIntegrationEnabled && !configLoading;

  return (
    <div className="harvesting-page-container">
      <HarvestingTopNav />

      {/* Log Your Harvesting Activity */}
      <div className="log-harvest-hero">
        <div className="log-harvest-hero-inner">
          <div className="log-harvest-hero-text">
            <div>
              <h3>Log Your Harvesting Activity</h3>
              <p>
                {showCipEmbed
                  ? 'CIP integration is active. Use CIP harvesting tools here, or use RS native logging and map links below when needed.'
                  : 'Record a harvest, count species, or report an environmental concern directly in the RSMIN portal. Your records are stored securely in our database — view, edit, or remove them at any time.'}
              </p>
            </div>
          </div>
          <div className="log-harvest-hero-actions">
            {useCipHarvesting ? (
              <Link to={embeddedHarvestReportPath} className="log-harvest-cta">
                + Log Harvesting Activity
              </Link>
            ) : (
              <>
                <Link to="/data/harvesting" className="log-harvest-cta">
                  + Log Harvesting Activity
                </Link>
                <Link to="/harvesting/map" className="tile-button secondary">
                  View Harvesting Map
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="harvesting-header">
        <h1>{landingPage?.title || 'Harvesting Portal'}</h1>
        <p className="subtitle">
          {landingPage?.subtitle || 'Resources, information and latest updates for RSMIN community harvesting'}
        </p>
      </div>

      {landingPage?.content && (
        <div
          className="landing-page-content"
          dangerouslySetInnerHTML={{ __html: landingPage.content }}
        />
      )}

      {/* Latest Blog Posts */}
      <div className="latest-blog-section">
        <div className="section-header">
          <h2>Latest Updates</h2>
          <div className="latest-blog-controls">
            <button
              type="button"
              className="latest-updates-toggle"
              onClick={() => setUpdatesCollapsed((prev) => !prev)}
              aria-expanded={!updatesCollapsed}
            >
              {updatesCollapsed ? 'Show Updates' : 'Hide Updates'}
            </button>
            <Link to={`/harvesting/blog${previewQuery}`} className="view-all-link">View All Posts</Link>
          </div>
        </div>

        {updatesCollapsed ? (
          <div className="latest-rollup">
            {loading && <p className="latest-rollup-message">Loading latest updates...</p>}
            {!loading && error && <p className="latest-rollup-message">{error}</p>}
            {!loading && !error && blogPosts.length === 0 && (
              <p className="latest-rollup-message">No updates posted yet.</p>
            )}
            {!loading && !error && blogPosts.length > 0 && (
              <ul className="latest-rollup-list">
                {blogPosts.map((post) => (
                  <li key={post._id} className="latest-rollup-item">
                    <Link to={`/harvesting/blog/${post._id}${previewQuery}`} className="latest-rollup-link">
                      <span>{post.title}</span>
                      <span className="latest-rollup-date">
                        {new Date(post.publishDate).toLocaleDateString('en-US', {
                          timeZone: 'America/Toronto',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : loading ? (
          <div className="loading-spinner">Loading posts...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : blogPosts.length === 0 ? (
          <p className="no-posts-message">No posts available at this time.</p>
        ) : (
          <div className="blog-post-cards">
            {blogPosts.map((post) => (
              <div key={post._id} className="blog-post-card">
                {post.imageUrl && (
                  <div className="blog-post-image">
                    <img src={post.imageUrl} alt={post.title} />
                  </div>
                )}
                <div className="post-date">
                  {new Date(post.publishDate).toLocaleDateString('en-US', {
                    timeZone: 'America/Toronto',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <h3>{post.title}</h3>
                <p className="post-excerpt">
                  {stripHtml(post.content).length > 120 
                    ? stripHtml(post.content).substring(0, 120) + '...' 
                    : stripHtml(post.content)}
                </p>
                <Link to={`/harvesting/blog/${post._id}${previewQuery}`} className="read-more-link">
                  Read More →
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HarvestingPage;
